import { useState } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Search, Mail, Calendar, CheckCircle2, Clock, MessageSquare, User } from 'lucide-react'
import { toast } from 'sonner'
import {
  useWaitlistUsers,
  useWaitlistInquiries,
  useUpdateAccessStatus,
  useMarkInquiryReviewed,
} from '@/hooks/useAdminWaitlist'

export function AdminWaitlistManager() {
  return (
    <Tabs defaultValue="users" className="w-full">
      <TabsList>
        <TabsTrigger value="users">Waitlist Users</TabsTrigger>
        <TabsTrigger value="inquiries">Inquiries</TabsTrigger>
      </TabsList>

      <TabsContent value="users" className="mt-6">
        <WaitlistUsersTab />
      </TabsContent>

      <TabsContent value="inquiries" className="mt-6">
        <InquiriesTab />
      </TabsContent>
    </Tabs>
  )
}

function WaitlistUsersTab() {
  const [searchQuery, setSearchQuery] = useState('')

  const { data: users, isLoading } = useWaitlistUsers({ search: searchQuery || undefined })
  const updateAccessStatus = useUpdateAccessStatus()

  const handleGrantAccess = async (userId: string, email: string) => {
    try {
      await updateAccessStatus.mutateAsync({
        userId,
        accessStatus: 'active',
      })
      toast.success(`Granted access to ${email}`)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      toast.error(`Failed to grant access: ${errorMessage}`)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading waitlist users...</p>
        </div>
      </div>
    )
  }

  if (!users || users.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No users on the waitlist</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Search */}
      <div className="space-y-2">
        <Label htmlFor="waitlist-search">Search Users</Label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="waitlist-search"
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">On Waitlist</p>
            <p className="text-2xl font-bold">{users.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">With Inquiries</p>
            <p className="text-2xl font-bold">
              {users.filter((u) => u.has_inquiry).length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">No Inquiries</p>
            <p className="text-2xl font-bold">
              {users.filter((u) => !u.has_inquiry).length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Users List */}
      <div className="space-y-2">
        {users.map((user) => (
          <div
            key={user.id}
            className="border rounded-lg p-4 hover:bg-accent/50 transition-colors"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{user.full_name || 'No name set'}</h3>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Mail className="h-3 w-3" />
                      {user.email}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 mt-3">
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Waitlist
                  </Badge>
                  {user.has_inquiry && (
                    <Badge variant="outline" className="flex items-center gap-1">
                      <MessageSquare className="h-3 w-3" />
                      {user.inquiry_count} {user.inquiry_count === 1 ? 'inquiry' : 'inquiries'}
                    </Badge>
                  )}
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    Signed up {new Date(user.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div className="flex flex-col items-end gap-2">
                <div className="text-xs text-muted-foreground">
                  ID: {user.id.slice(0, 8)}...
                </div>
                <Button
                  size="sm"
                  onClick={() => handleGrantAccess(user.id, user.email)}
                  disabled={updateAccessStatus.isPending}
                >
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  Grant Access
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function InquiriesTab() {
  const [reviewedFilter, setReviewedFilter] = useState<'all' | 'reviewed' | 'pending'>('pending')

  const { data: inquiries, isLoading } = useWaitlistInquiries({
    reviewed: reviewedFilter === 'all' ? undefined : reviewedFilter === 'reviewed',
  })
  const markReviewed = useMarkInquiryReviewed()
  const updateAccessStatus = useUpdateAccessStatus()

  const handleMarkReviewed = async (inquiryId: string, userEmail: string) => {
    try {
      await markReviewed.mutateAsync(inquiryId)
      toast.success(`Marked inquiry from ${userEmail} as reviewed`)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      toast.error(`Failed to mark as reviewed: ${errorMessage}`)
    }
  }

  const handleGrantAccess = async (userId: string, email: string) => {
    try {
      await updateAccessStatus.mutateAsync({
        userId,
        accessStatus: 'active',
      })
      toast.success(`Granted access to ${email}`)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      toast.error(`Failed to grant access: ${errorMessage}`)
    }
  }

  const reasonLabels: Record<string, string> = {
    urgent_project: 'Urgent Project',
    questions: 'Questions',
    status_check: 'Status Check',
    other: 'Other',
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading inquiries...</p>
        </div>
      </div>
    )
  }

  if (!inquiries || inquiries.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No inquiries found</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Filter */}
      <div className="flex gap-2">
        <Button
          variant={reviewedFilter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setReviewedFilter('all')}
        >
          All
        </Button>
        <Button
          variant={reviewedFilter === 'pending' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setReviewedFilter('pending')}
        >
          Pending
        </Button>
        <Button
          variant={reviewedFilter === 'reviewed' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setReviewedFilter('reviewed')}
        >
          Reviewed
        </Button>
      </div>

      {/* Inquiries List */}
      <div className="space-y-4">
        {inquiries.map((inquiry) => (
          <div
            key={inquiry.id}
            className="border rounded-lg p-4 space-y-3"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 space-y-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold">{inquiry.user_full_name || 'No name'}</h3>
                  <Badge variant="secondary">{reasonLabels[inquiry.reason]}</Badge>
                  {inquiry.is_reviewed && (
                    <Badge variant="outline" className="flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      Reviewed
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Mail className="h-3 w-3" />
                  {inquiry.user_email}
                </div>
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {new Date(inquiry.created_at).toLocaleDateString()} at{' '}
                  {new Date(inquiry.created_at).toLocaleTimeString()}
                </span>
              </div>

              <div className="flex gap-2">
                {!inquiry.is_reviewed && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleMarkReviewed(inquiry.id, inquiry.user_email)}
                    disabled={markReviewed.isPending}
                  >
                    Mark Reviewed
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={() => handleGrantAccess(inquiry.user_id, inquiry.user_email)}
                  disabled={updateAccessStatus.isPending}
                >
                  Grant Access
                </Button>
              </div>
            </div>

            {inquiry.message && (
              <div className="bg-muted/50 rounded p-3 text-sm">
                <p className="font-medium text-xs text-muted-foreground mb-1">Message:</p>
                <p className="whitespace-pre-wrap">{inquiry.message}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
